import { Button, Loader, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { listActions } from "graphql/queries";
import { deleteAction } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { ActionCreateForm, ActionUpdateForm } from ".";

const Actions = () => {
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "action:create",
    "action:update",
    "action:delete",
  ]);
  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionDetails, setActionDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    actionId: null,
    actionName: null,
  });
  const [modalAction, setModalAction] = useState({
    visible: false,
    actionId: null,
  });
  const columnsMapped = useMemo(
    () =>
      actions.map((item) => ({
        ...item,
        actions: (
          <>
            <Button
              className="px-1"
              children={
                actionDetails?.id === item.id
                  ? strings.actions.hide
                  : strings.actions.show
              }
              variation="link"
              size="small"
              onClick={() => {
                actionDetails?.id === item.id
                  ? setActionDetails(null)
                  : setActionDetails(item);
              }}
            />
            {canUpdate && (
              <Button
                className="px-1"
                children={strings.actions.edit}
                variation="link"
                size="small"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <Button
                className="px-1"
                children={strings.actions.delete}
                variation="link"
                size="small"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    actionId: item.id,
                    actionName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actions, actionDetails?.id]
  );

  const onNext = () => {
    fetchActions(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchActions(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchActions(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalAction({ visible: true, action, actionId: id });
  const onSuccessAction = () => {
    setModalAction({ visible: false });
    fetchActions();
  };

  async function fetchActions(token, isNext = true, keywords) {
    try {
      setActionsLoading(true);
      const actionsData = await API.graphql(
        graphqlOperation(listActions, {
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      shortName: { contains: key },
                    },
                    {
                      description: { contains: key },
                    },
                  ],
                })),
              }
            : {},
        })
      );
      const actionsItems = actionsData.data.listActions.items;
      setActions(actionsItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: actionsData.data.listActions.nextToken,
        };
      });
      setActionsLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setActionsLoading(false);
    }
  }
  async function actionDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(deleteAction, { input: { id } })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.deleteAction.id;
      setActions(actions.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        actionId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableColumns = [
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "30%" },
    },
    {
      key: "shortName",
      label: strings.table.headers.shortName,
      _style: { width: "20%" },
    },
    {
      key: "description",
      label: strings.table.headers.description,
      _style: { width: "30%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.actions.addAction}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <div style={styles.searchByKeyword}>
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </div>
        {actionsLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!actionDetails?.id} style={styles.collapseCard}>
          <CCard key={actionDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{actionDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.shortName}:</b> {actionDetails?.shortName}
              </p>
              <p className="text-muted">
                <b>{strings.details.description}:</b>{" "}
                {actionDetails?.description}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(actionDetails?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <CModal
        alignment="center"
        visible={modalDetails.visible}
        onClose={() => setModalDetails({ visible: false, actionId: null })}
        backdrop="static"
      >
        <CModalBody>
          <b>
            {strings.formatString(strings.modal.confirmDeleteQuestion, {
              name: modalDetails.actionName,
            })}
          </b>
        </CModalBody>
        <CModalFooter>
          <Button
            size="small"
            onClick={() => setModalDetails({ visible: false, actionId: null })}
          >
            {strings.modal.cancelButton}
          </Button>
          <Button
            size="small"
            variation="primary"
            onClick={() => actionDelete(modalDetails.actionId)}
          >
            {strings.modal.confirmButton}
          </Button>
        </CModalFooter>
      </CModal>
      {modalAction.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalAction.visible}
          onClose={() => setModalAction({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalAction.action === "create"
                ? strings.modalAction.addTitle
                : strings.modalAction.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalAction.action === "create" ? (
                <ActionCreateForm onSuccess={onSuccessAction} />
              ) : (
                <ActionUpdateForm
                  actionId={modalAction.actionId}
                  onSuccess={onSuccessAction}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Acciones",
    successDeleted: "Acción eliminada con éxito",
    actions: {
      addAction: "Agregar Acción",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar acciones",
    },
    table: {
      headers: {
        name: "Nombre",
        shortName: "Nombre corto",
        description: "Descripción",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles de la Acción",
      shortName: "Nombre corto",
      description: "Descripción",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar la acción {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalAction: {
      addTitle: "Agregar Acción",
      updateTitle: "Actualizar Acción",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Actions;
